import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import unified from 'unified';

import theme from '../assets/theme';
import {
  CuripodDecendant,
  CuripodElement,
  CuripodText,
} from '../components/SlideComponents/slateUtil';
import remarkSlatePlugin from './remarkSlatePlugin/remarkSlate';

// Enhanced parseMarkdown function that supports bold, italic, underline and nested combinations
export const parseMarkdownEnhanced = (text: string): CuripodDecendant[] => {
  // Updated regex to handle nested formatting patterns
  const regex = /(\*\*\*([^*]*?)\*\*\*)|(\*\*_([^_]*?)_\*\*)|(_\*\*([^*]*?)\*\*_)|(\*\*([^*]*?)\*\*)|(__\*([^*]*?)\*__)|(\*__([^_]*?)__\*)|(\*([^*]*?)\*)|(__([^_]*?)__)/g;
  let match;
  const result: CuripodText[] = [];
  let lastIndex = 0;

  // Loop through all matches of various formatting combinations
  while ((match = regex.exec(text)) !== null) {
    // Add text before the markdown part
    if (match.index > lastIndex) {
      result.push({
        text: text.slice(lastIndex, match.index),
        color: theme.colors.black,
      });
    }

    let matchedText = '';
    let isBold = false;
    let isItalic = false;
    let isUnderline = false;

    // Determine which group was matched and set appropriate flags
    if (match[1]) {
      // Matched ***italic+bold***
      matchedText = match[2];
      isBold = true;
      isItalic = true;
    } else if (match[3]) {
      // Matched **_bold+underline_**
      matchedText = match[4];
      isBold = true;
      isUnderline = true;
    } else if (match[5]) {
      // Matched _**underline+bold**_
      matchedText = match[6];
      isBold = true;
      isUnderline = true;
    } else if (match[7]) {
      // Matched **bold**
      matchedText = match[8];
      isBold = true;
    } else if (match[9]) {
      // Matched __*underline+italic*__
      matchedText = match[10];
      isItalic = true;
      isUnderline = true;
    } else if (match[11]) {
      // Matched *__italic+underline__*
      matchedText = match[12];
      isItalic = true;
      isUnderline = true;
    } else if (match[13]) {
      // Matched *italic*
      matchedText = match[14];
      isItalic = true;
    } else if (match[15]) {
      // Matched __underline__
      matchedText = match[16];
      isUnderline = true;
    }

    // Add the styled part
    result.push({
      text: matchedText,
      bold: isBold || undefined,
      italic: isItalic || undefined,
      underline: isUnderline || undefined,
      color: theme.colors.black,
    });

    lastIndex = regex.lastIndex;
  }

  // Add any remaining text after the last styled section
  if (lastIndex < text.length) {
    result.push({ text: text.slice(lastIndex), color: theme.colors.black });
  }

  // Prevent an empty children in the array. As slate crashes if children is empty
  if (result.length === 0) {
    result.push({
      text: '',
      color: theme.colors.black,
    });
  }

  return result;
};

// New markdown to slate function that supports headers, bold, italic, underline
export const markdownToSlateEnhanced = (
  markdown: string,
  defaultFontSize = 14,
): CuripodDecendant[] => {
  const blocks: CuripodDecendant[] = [];

  // Split input text by new lines and handle various line break formats
  // DON'T trim or filter empty lines - we want them to become empty paragraphs
  const lines = markdown.replace(/(?:\\n|\\r\\n|<br\s*\/?>)/g, '\n').split('\n');

  const getHeaderFontSize = (level: number, baseFontSize: number): number => {
    // Header font sizes based on level (h1 largest, h6 smallest)
    const sizeMultipliers: Record<number, number> = {
      1: 1.2, // h1 - largest
      2: 1.15, // h2
      3: 1.1, // h3
      4: 1.05, // h4
      5: 1.0, // h5
      6: 0.9, // h6 - smallest
    };
    return Math.round(baseFontSize * (sizeMultipliers[level] || 1));
  };

  const createEmptyParagraphBlock = (): CuripodElement => {
    return {
      type: 'paragraph',
      color: theme.colors.black,
      size: defaultFontSize,
      align: 'left',
      children: [{ text: '', color: theme.colors.black }],
    };
  };

  const createHeaderBlock = (text: string, level: number): CuripodElement => {
    const fontSize = getHeaderFontSize(level, defaultFontSize);
    return {
      type: 'paragraph',
      color: theme.colors.black,
      size: fontSize,
      align: 'left',
      children: parseMarkdownEnhanced(text),
    };
  };

  const createParagraphBlock = (text: string): CuripodElement => {
    return {
      type: 'paragraph',
      color: theme.colors.black,
      size: defaultFontSize,
      align: 'left',
      children: parseMarkdownEnhanced(text),
    };
  };

  const createListItem = (text: string): CuripodElement => {
    return {
      type: 'list-item',
      align: 'left',
      size: defaultFontSize,
      children: parseMarkdownEnhanced(text),
    };
  };

  let currentListBlock: CuripodElement | null = null;
  let listType: 'bulleted-list' | 'numbered-list' | null = null;

  const finishCurrentList = () => {
    if (currentListBlock) {
      blocks.push(currentListBlock);
      currentListBlock = null;
      listType = null;
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Empty line - create empty paragraph and finish any current list
    if (trimmedLine.length === 0) {
      finishCurrentList();
      blocks.push(createEmptyParagraphBlock());
      continue;
    }

    // Check for headers
    const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      finishCurrentList();
      const level = headerMatch[1].length;
      const headerText = headerMatch[2];
      blocks.push(createHeaderBlock(headerText, level));
      continue;
    }

    // Check for bullet points
    const bulletMatch = trimmedLine.match(/^[-*+]\s+(.*)$/);
    if (bulletMatch) {
      const itemText = bulletMatch[1];

      if (!currentListBlock || listType !== 'bulleted-list') {
        finishCurrentList();
        currentListBlock = {
          type: 'bulleted-list',
          color: theme.colors.black,
          size: defaultFontSize,
          children: [createListItem(itemText)],
        };
        listType = 'bulleted-list';
      } else if (currentListBlock.type === 'bulleted-list') {
        currentListBlock.children.push(createListItem(itemText));
      }
      continue;
    }

    // Check for numbered lists
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
    if (numberedMatch) {
      const itemText = numberedMatch[2];

      if (!currentListBlock || listType !== 'numbered-list') {
        finishCurrentList();
        currentListBlock = {
          type: 'numbered-list',
          color: theme.colors.black,
          size: defaultFontSize,
          children: [createListItem(itemText)],
        };
        listType = 'numbered-list';
      } else if (currentListBlock.type === 'numbered-list') {
        currentListBlock.children.push(createListItem(itemText));
      }
      continue;
    }

    // Regular paragraph
    finishCurrentList();
    blocks.push(createParagraphBlock(trimmedLine));
  }

  // Finish any remaining list
  finishCurrentList();

  // Ensure we always return at least one block with valid content
  if (blocks.length === 0) {
    blocks.push(createEmptyParagraphBlock());
  }

  return blocks;
};

// New markdown to slate function that supports headers, bold, italic, underline used in learning objective refinement
export const markdownToSlateEnhancedLearningObjective = (
  markdown: string,
  defaultFontSize = 14,
): CuripodDecendant[] => {
  const blocks: CuripodDecendant[] = [];

  // Split input text by new lines and handle various line break formats
  // DON'T trim or filter empty lines - we want them to become empty paragraphs
  const lines = markdown.replace(/(?:\\n|\\r\\n|<br\s*\/?>)/g, '\n').split('\n');

  const getHeaderFontSize = (level: number, baseFontSize: number): number => {
    // Header font sizes based on level (h1 largest, h6 smallest)
    const sizeMultipliers: Record<number, number> = {
      1: 1.2, // h1 - largest
      2: 1.15, // h2
      3: 1.1, // h3
      4: 1.05, // h4
      5: 1.0, // h5
      6: 0.9, // h6 - smallest
    };
    return Math.round(baseFontSize * (sizeMultipliers[level] || 1));
  };

  const createEmptyParagraphBlock = (): CuripodElement => {
    return {
      type: 'paragraph',
      color: theme.colors.black,
      size: defaultFontSize,
      align: 'left',
      children: [{ text: '', color: theme.colors.black }],
    };
  };

  const createHeaderBlock = (text: string, level: number): CuripodElement => {
    const fontSize = getHeaderFontSize(level, defaultFontSize);
    return {
      type: 'paragraph',
      color: theme.colors.black,
      size: fontSize,
      align: 'left',
      children: parseMarkdownEnhanced(text),
    };
  };

  const createParagraphBlock = (text: string): CuripodElement => {
    return {
      type: 'paragraph',
      color: theme.colors.black,
      size: defaultFontSize,
      align: 'left',
      children: parseMarkdownEnhanced(text),
    };
  };

  const createListItem = (text: string): CuripodElement => {
    return {
      type: 'list-item',
      align: 'left',
      size: defaultFontSize,
      children: parseMarkdownEnhanced(text),
    };
  };

  let currentListBlock: CuripodElement | null = null;
  let listType: 'bulleted-list' | 'numbered-list' | null = null;

  const finishCurrentList = () => {
    if (currentListBlock) {
      blocks.push(currentListBlock);
      currentListBlock = null;
      listType = null;
    }
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    // Empty line - handle spacing but preserve numbered lists (don't finish them)
    if (trimmedLine.length === 0) {
      // Only finish the current list if it's a bulleted list, preserve numbered lists
      if (currentListBlock && listType === 'bulleted-list') {
        finishCurrentList();
      }
      // Don't create empty paragraphs for every empty line to reduce excessive spacing
      // Only create empty paragraphs if we're not currently in a list and the last block isn't already empty
      if (!currentListBlock && blocks.length > 0) {
        const lastBlock = blocks[blocks.length - 1] as CuripodElement;
        if (
          lastBlock.type !== 'paragraph' ||
          (lastBlock.children.length > 0 &&
            (lastBlock.children[0] as CuripodText).text !== '')
        ) {
          blocks.push(createEmptyParagraphBlock());
        }
      }
      continue;
    }

    // Check for headers
    const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
    if (headerMatch) {
      finishCurrentList();
      const level = headerMatch[1].length;
      const headerText = headerMatch[2];
      blocks.push(createHeaderBlock(headerText, level));
      continue;
    }

    // Check for indented bullet points (sub-bullets within numbered lists)
    const indentedBulletMatch = line.match(/^(\s+)[-*+•]\s+(.*)$/);
    if (indentedBulletMatch && currentListBlock && listType === 'numbered-list') {
      // Add indented bullet as text content to the last numbered list item
      const itemText = indentedBulletMatch[2];
      const indent = indentedBulletMatch[1];
      const lastListItem = currentListBlock.children[
        currentListBlock.children.length - 1
      ] as CuripodElement;

      if (lastListItem && lastListItem.type === 'list-item') {
        // Parse the bullet text with markdown formatting
        const parsedBulletText = parseMarkdownEnhanced(`${indent}• ${itemText}`);

        // Add to the existing item's children
        if (lastListItem.children.length > 0) {
          // Add a line break first, then the parsed content
          const lineBreak: CuripodText = { text: '\n', color: theme.colors.black };
          lastListItem.children.push(lineBreak, ...parsedBulletText);
        } else {
          lastListItem.children.push(...parsedBulletText);
        }
      }
      continue;
    }

    // Check for bullet points
    const bulletMatch = trimmedLine.match(/^[-*+•]\s+(.*)$/);
    if (bulletMatch) {
      const itemText = bulletMatch[1];

      // If we're currently in a numbered list, add bullets as sub-content instead of creating new list
      if (currentListBlock && listType === 'numbered-list') {
        const lastListItem = currentListBlock.children[
          currentListBlock.children.length - 1
        ] as CuripodElement;

        if (lastListItem && lastListItem.type === 'list-item') {
          // Parse the bullet text with markdown formatting
          const parsedBulletText = parseMarkdownEnhanced(`• ${itemText}`);

          // Add to the existing item's children
          if (lastListItem.children.length > 0) {
            // Add a line break first, then the parsed content
            const lineBreak: CuripodText = { text: '\n', color: theme.colors.black };
            lastListItem.children.push(lineBreak, ...parsedBulletText);
          } else {
            lastListItem.children.push(...parsedBulletText);
          }
        }
        continue;
      }

      // Otherwise, create a new bulleted list
      if (!currentListBlock || listType !== 'bulleted-list') {
        finishCurrentList();
        currentListBlock = {
          type: 'bulleted-list',
          color: theme.colors.black,
          size: defaultFontSize,
          children: [createListItem(itemText)],
        };
        listType = 'bulleted-list';
      } else if (currentListBlock.type === 'bulleted-list') {
        currentListBlock.children.push(createListItem(itemText));
      }
      continue;
    }

    // Check for numbered lists (ignore the actual number, we'll auto-increment)
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
    if (numberedMatch) {
      const itemText = numberedMatch[2];

      // Always continue the numbered list if one exists, regardless of the number in the source
      if (currentListBlock && listType === 'numbered-list') {
        currentListBlock.children.push(createListItem(itemText));
      } else {
        finishCurrentList();
        currentListBlock = {
          type: 'numbered-list',
          color: theme.colors.black,
          size: defaultFontSize,
          children: [createListItem(itemText)],
        };
        listType = 'numbered-list';
      }
      continue;
    }

    // Regular paragraph
    finishCurrentList();
    blocks.push(createParagraphBlock(trimmedLine));
  }

  // Finish any remaining list
  finishCurrentList();

  // Ensure we always return at least one block with valid content
  if (blocks.length === 0) {
    blocks.push(createEmptyParagraphBlock());
  }

  return blocks;
};

export const parseMarkdownToSlateV2 = async (
  markdown: string,
): Promise<CuripodDecendant[]> => {
  const virtualFile = await unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkMath)
    .use(remarkSlatePlugin, {
      defaults: {
        fontSize: 14,
        color: theme.colors.black,
        align: 'left',
      },
    })
    .process(markdown);

  return virtualFile.result as CuripodDecendant[];
};
